import React from 'react';

interface Props {
  markdown: string;
}

const MarkdownRenderer = ({ markdown }: Props) => {
  const renderLine = (line: string) => {
    // Handle links with arrow symbol
    line = line.replace(/\[(.*?)\]\((.*?)\)/g, (_, text, url) => {
      // Replace the marker with the SVG
      text = text.replace(
        '{{external-link}}',
        '<svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 inline-block" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>'
      );
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 text-primary underline">${text}</a>`;
    });
    // Handle bold text
    line = line.replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold">$1</strong>');
    // Handle italic text
    line = line.replace(/\*(.*?)\*/g, '<em class="italic">$1</em>');

    // AI sometimes starts and ends a message with quotes "" which should be removed
    line = line.replace(/^"|"$/g, '');

    // Remove bracketed content like [AI] or [ASSISTANT] from messages
    line = line.replace(/\[[^\]]+\]/g, '');

    return line;
  };

  const renderList = (items: string[], ordered: boolean) => {
    const ListTag = ordered ? 'ol' : 'ul';
    const listClass = ordered ? 'list-decimal pl-5 mb-4' : 'list-disc pl-5 mb-4';

    return (
      <ListTag className={listClass}>
        {items.map((item, index) => (
          <li key={index} className="mb-1" dangerouslySetInnerHTML={{ __html: renderLine(item) }} />
        ))}
      </ListTag>
    );
  };

  const renderContent = () => {
    const lines = markdown.split('\n');
    const result = [];
    let currentList: string[] = [];
    let isOrdered = false;

    lines.forEach((line, index) => {
      // Check for list items first
      const trimmedLine = line.trim();
      if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
        if (currentList.length > 0 && isOrdered) {
          // End previous ordered list if starting an unordered one
          result.push(renderList(currentList, true));
          currentList = [];
        }
        currentList.push(trimmedLine.slice(2));
        isOrdered = false;
      } else if (/^\d+\.\s/.test(trimmedLine)) {
        // Simpler check for standard ordered list (e.g., 1. item)
        if (currentList.length > 0 && !isOrdered) {
          // End previous unordered list if starting an ordered one
          result.push(renderList(currentList, false));
          currentList = [];
        }
        // Remove the "1. " prefix
        currentList.push(trimmedLine.replace(/^\d+\.\s/, ''));
        isOrdered = true;
      } else {
        // End of any current list
        if (currentList.length > 0) {
          result.push(renderList(currentList, isOrdered));
          currentList = [];
        }

        // Now handle paragraphs and empty lines
        if (line === '') {
          // Render an empty paragraph to create a visual break (adjust height as needed)
          result.push(<p key={`empty-${index}`} className="h-4"></p>);
        } else if (trimmedLine !== '') {
          // Render non-empty, non-list lines as paragraphs
          result.push(
            <p
              key={index}
              className="leading-relaxed"
              dangerouslySetInnerHTML={{ __html: renderLine(trimmedLine) }}
            />
          );
        }
        // Ignore lines that only contain whitespace
      }
    });

    // Handle any remaining list items at the end of the markdown
    if (currentList.length > 0) {
      result.push(renderList(currentList, isOrdered));
    }

    return result;
  };

  return <div className="custom-markdown">{renderContent()}</div>;
};

export default MarkdownRenderer;
